
import { l3draggable, l3droppable } from '@/directives/l3dragDrop';
import { Component, Vue, Prop, PropSync, Watch } from 'vue-property-decorator';
// components
import SimpleMessageBox from './SimpleMessageBox.vue';
import TusFileUploadOverlay from './TusFileUploadOverlay.vue';
import ImageAiDlg from './ImageAiDlg.vue';
// interfaces
import { FileDetailsVm } from '@/interfaces/FileDetailsVm';
import { namespace } from 'vuex-class';
import { ExBundleVM } from '@/interfaces/ExBundleAssignmentGroupVM';
import { CustomizationVm } from '@/interfaces/CustomizationVm';

const globals = namespace("globals");
const customization = namespace("customization");

@Component({
  components: {
    TusFileUploadOverlay,
    ImageAiDlg,
    SimpleMessageBox
  },
  directives: { l3draggable, l3droppable }
})
export default class ImageWithDnD extends Vue {
  @PropSync("image", { type: String, default: "" }) syncedImage!: string | null;
  @PropSync("prompt") syncedPrompt!: string | null;
  @PropSync("changePrompt") syncedChangePrompt!: string | null;
  @Prop({ default: true }) enableAltText!: boolean;
  @PropSync("altText") syncedAltText!: string | null;
  @PropSync("imageLibrarySearchText") syncedImageLibrarySearchText!: string | null;
  @Prop() relativePath!: string; // optional relative path to upload file to (e.g. "images/" or "documents/")
  @Prop({ type: [Boolean], default: false }) allowDropOnImage!: boolean;
  @Prop({ type: [Boolean], default: false }) emitDeleteEvent!: boolean;
  @Prop({ type: [Boolean], default: false }) fillContainer!: boolean;
  @PropSync("aspectRatio") syncedAspectRatio!: string | null;
  @PropSync("imageStyle") syncedImageStyle!: string | null;
  @Prop({ type: [Boolean], default: false }) uploadImage!: boolean;
  @Prop({ type: [Number], default: null }) previewHeight!: number | null;
  @Prop({ type: String, default: 'center', validator: (v: string) => ['start','top','center','middle','end','bottom'].includes(v) })
  verticalAlign!: string;
  @Prop({ type: [Number], default: 0 }) maxImageWidth!: number;
  @Prop({ type: [Number], default: 0 }) maxImageHeight!: number;
  @Prop({ type: [Boolean], default: false }) disabled!: boolean;

  // @globals.Getter selectedBundle!: ExBundleVM
  @customization.Getter customizationSettings!: CustomizationVm;

  // Calculated dimensions for the displayed image while respecting maxImageWidth/maxImageHeight
  imageDisplayWidth: number | null = null;
  imageDisplayHeight: number | null = null;
  fileToUpload: File | null = null;
  showAIGenerator = false;
  showConfirmDelete = false;
  imageLoadError: boolean = false;
  showDropNotAllowed: boolean = false;

  @Watch('syncedImage', { immediate: true })
  onSyncedImageChanged(val: string | null) {
    if (!val) {
      this.imageDisplayWidth = null;
      this.imageDisplayHeight = null;
      this.imageLoadError = false;
      return;
    }
    this.updateImageDisplayWidth(val);
  }

  @Watch('previewHeight')
  onPreviewHeightChanged() {
    if (this.syncedImage) {
      this.updateImageDisplayWidth(this.syncedImage);
    }
  }

  updateImageDisplayWidth(src: string | null) {
    if (!src) {
      this.imageDisplayWidth = null;
      this.imageDisplayHeight = null;
      this.imageLoadError = false;
      return;
    }

    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || img.width || 0;
      const h = img.naturalHeight || img.height || 0;
      if (!w || !h) {
        this.imageDisplayWidth = null;
        this.imageDisplayHeight = null;
        return;
      }

      const maxWidth = this.maxImageWidth > 0 ? this.maxImageWidth : Number.MAX_SAFE_INTEGER;
      const maxHeight = this.maxImageHeight > 0 ? this.maxImageHeight : Number.MAX_SAFE_INTEGER;
      const previewHeightLimit = this.previewHeight != null ? Math.min(this.previewHeight, maxHeight) : maxHeight;
      const widthLimit = maxWidth;
      const heightLimit = previewHeightLimit;

      const scale = Math.min(1, widthLimit / w, heightLimit / h);
      this.imageDisplayWidth = Math.max(1, Math.round(w * scale));
      this.imageDisplayHeight = Math.max(1, Math.round(h * scale));
      this.imageLoadError = false;
    };
    img.onerror = () => {
      this.imageDisplayWidth = null;
      this.imageDisplayHeight = null;
      this.imageLoadError = true;
    };
    img.src = src;
  }

  private isImageDropData(data: any, dt: DataTransfer | null): boolean {
    if (data?.fileType && data.fileType === 'Image') return true;

    if (Array.isArray(data) && data[0]?.name && data[0]?.type) {
      return data[0].type.startsWith('image/');
    }

    if (!dt) return false;

    try {
      if (dt.items && dt.items.length) {
        for (const item of Array.from(dt.items)) {
          if (item?.kind === 'file') {
            const type = item.type || '';
            return type.startsWith('image/') || type === '';
          }
        }
      }

      if (dt.types && Array.from(dt.types).includes('Files')) {
        return true;
      }

      if (this.isWebImageDrag(dt)) {
        return true;
      }
    } catch (ex) {
      return false;
    }

    return false;
  }

  // Image dragged from a web page (e.g. Unsplash) without file data: only url and html are transferred
  private isWebImageDrag(dt: DataTransfer | null): boolean {
    if (!dt?.types) return false;
    const types = Array.from(dt.types);
    return !types.includes('application/json') && (types.includes('text/uri-list') || types.includes('text/html'));
  }

  // A broken image url shows the empty placeholder, so it must not block drops
  get hasVisibleImage(): boolean {
    return !!this.syncedImage && !this.imageLoadError;
  }

  private isBlockedDropData(data: any, dt: DataTransfer | null): boolean {
    if (!this.hasVisibleImage || this.allowDropOnImage || this.disabled) {
      return false;
    }

    return this.isImageDropData(data, dt);
  }

  private updateDropState(data: any, dt: DataTransfer | null): boolean {
    const blocked = this.isBlockedDropData(data, dt);
    this.showDropNotAllowed = blocked;
    return blocked;
  }

  handleNativeDragEnter(e: DragEvent) {
    const blocked = this.updateDropState(undefined, e.dataTransfer);

    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
          e.dataTransfer.effectAllowed = 'none';
        }
      } catch {}
      return;
    }

    this.showDropNotAllowed = false;
  }

  handleNativeDragOver(e: DragEvent) {
    const blocked = this.updateDropState(undefined, e.dataTransfer);

    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      try {
        if (e.dataTransfer) {
          e.dataTransfer.dropEffect = 'none';
          e.dataTransfer.effectAllowed = 'none';
        }
      } catch {}
      return;
    }

    this.showDropNotAllowed = false;
  }

  handleNativeDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    this.showDropNotAllowed = false;
  }

  handleNativeDragLeave() {
    this.showDropNotAllowed = false;
  }

  get shouldShowDropNotAllowed(): boolean {
    return this.showDropNotAllowed && this.hasVisibleImage && !this.allowDropOnImage && !this.disabled;
  }

  get containerStyle() {
    const alignMap: { [key: string]: string } = {
      start: 'flex-start',
      top: 'flex-start',
      center: 'center',
      middle: 'center',
      end: 'flex-end',
      bottom: 'flex-end'
    };
    const align = alignMap[this.verticalAlign] || 'center';

    if (this.previewHeight == null) {
      if (this.fillContainer) {
        const style: any = { display: 'flex', alignItems: align, justifyContent: 'center', width: '100%', height: '100%' };
        if (this.maxImageWidth > 0) style.maxWidth = `${this.maxImageWidth}px`;
        if (this.maxImageHeight > 0) style.maxHeight = `${this.maxImageHeight}px`;
        return style;
      }
      return { display: 'flex', alignItems: align, justifyContent: 'center', width: '100%', height: '100%' };
    }

    // Empty state: keep a stable drop/select area
    if (!this.syncedImage) {
      const emptyHeight = this.previewHeight != null
        ? (this.maxImageHeight > 0 ? Math.min(this.previewHeight, this.maxImageHeight) : this.previewHeight)
        : (this.maxImageHeight > 0 ? this.maxImageHeight : 0);
      return {
        display: 'flex',
        alignItems: align,
        justifyContent: 'center',
        width: this.maxImageWidth > 0 ? `${this.maxImageWidth}px` : '100%',
        height: `${emptyHeight}px`
      };
    }

    // Image state: container width matches visible image width => overlay doesn't span extra space
    if (this.fillContainer) {
      const style: any = {
        display: 'flex',
        alignItems: align,
        justifyContent: 'center',
        width: '100%',
        height: `${this.previewHeight}px`
      };
      if (this.maxImageWidth > 0) style.maxWidth = `${this.maxImageWidth}px`;
      if (this.maxImageHeight > 0) style.maxHeight = `${this.maxImageHeight}px`;
      return style;
    }

    // Height follows the render box, so the container shrinks with the image if less width is available
    return {
      display: 'flex',
      alignItems: align,
      justifyContent: 'center',
      width: this.imageDisplayWidth ? `${this.imageDisplayWidth}px` : 'auto',
      height: 'auto'
    };
  }

  // Without fillContainer the image keeps its aspect ratio and shrinks to fit into the available space
  get renderBoxStyle() {
    if (this.fillContainer) {
      return { width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' };
    }
    if (!this.imageDisplayWidth || !this.imageDisplayHeight) {
      return { width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' };
    }
    return {
      width: `${this.imageDisplayWidth}px`,
      height: 'auto',
      maxWidth: '100%',
      maxHeight: '100%',
      aspectRatio: `${this.imageDisplayWidth} / ${this.imageDisplayHeight}`
    };
  }

  get vImgStyle() {
    const map: { [k: string]: string } = { start: 'top', top: 'top', center: 'center', middle: 'center', end: 'bottom', bottom: 'bottom' };
    const pos = map[this.verticalAlign] || 'center';
    return { backgroundPosition: `${pos} center`, objectPosition: `${pos} center` };
  }

  onUploaded(filePath: string | null) {
    if (!filePath) return;
    this.syncedImage = filePath;
    //console.log("Image uploaded successfully, path:", filePath);
    this.syncedAltText = ""; // reset alt text when new image is uploaded
    this.imageLoadError = false;
  }

  onCancelled() {
    // this.fileToUpload = null;
  }

  // open hidden file input
  openFileDialog() {
    const input = this.$refs.fileInput as HTMLInputElement | undefined;
    if (input) input.click();
  }

  onOpenUnsplash() {
    const url = `https://unsplash.com/s/photos/${this.syncedImageLibrarySearchText || 'education'}?orientation=landscape`;
    if (typeof window !== 'undefined' && window.open) {
      window.open(url, '_blank', 'noopener,noreferrer,width=1000,height=800');
    } else {
      // no window available (SSR) — fallback: log or handle gracefully
      // eslint-disable-next-line no-console
      console.warn('Cannot open Unsplash: no window object available');
    }
  }

  onOpenAIGenerator() {
    this.showAIGenerator = true;
  }

  onDownloadFile() {
    if (!this.syncedImage) return;
    let filename = this.$globalHelper.extractFileName(this.syncedImage);
    this.$globalHelper.download(this.syncedImage, filename);
  }

  // handle file selection and set base64 image
  onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input || !input.files || input.files.length === 0) return;
    const file = input.files[0];
    if (!file.type.startsWith('image/')) {
      this.$store.commit("ux/SB_FAILURE", {
        message: "Bei dieser Datei handelt es sich nicht um ein Bild.",
        timeout: 0
      });
      input.value = '';
      return;
    }
    // Upload to server like a dropped file, otherwise the base64 image is stored in the content
    if (this.uploadImage) {
      this.fileToUpload = file; // trigger upload overlay
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev: any) => {
      this.syncedImage = ev.target.result;
      this.updateImageDisplayWidth(this.syncedImage);
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  onDeleteImage() {
    if (this.emitDeleteEvent) {
      this.$emit('delete:imagewithdnd');
      return;
    }
    this.syncedImage = null;
    this.imageLoadError = false;
  }

  onVImgError() {
    this.imageLoadError = true;
    this.imageDisplayWidth = null;
  }

  onVImgLoad() {
    this.imageLoadError = false;
  }

  // determine if Vuetify is in dark theme
  get isDark(): boolean {
    try {
      // Vuetify exposes theme state on $vuetify.theme.dark
      return !!((this as any).$vuetify && (this as any).$vuetify.theme && (this as any).$vuetify.theme.dark);
    } catch (e) {
      return false;
    }
  }

  // overlay styling depends on theme (dark / light)
  get overlayStyle() {
    return {
      background: this.isDark ? 'rgba(50,50,50,0.85)' : 'rgba(200,200,200,0.85)'
    };
  }

  get dropOptions() {
    return {
      accept: (data: any, ev?: DragEvent) => {
        const hasFileTransfer = !!ev?.dataTransfer?.types?.includes('Files');
        const isImageDrop = this.isImageDropData(data, ev?.dataTransfer ?? null);
        const blocked = this.hasVisibleImage && !this.allowDropOnImage && !this.disabled && isImageDrop;
        const accepted = !blocked && (isImageDrop || hasFileTransfer);
        return accepted;
      },
      hoverClass: "drop-hover",
      transferTypes: ["Files", "text/uri-list", "text/html"],
      targetGroups: ["image-file-details"],
      onDragEnter: (ev?: DragEvent) => {
        this.updateDropState(undefined, ev?.dataTransfer ?? null);
      },
      onDragOver: (ev?: DragEvent) => {
        this.updateDropState(undefined, ev?.dataTransfer ?? null);
      },
      onDrop: async (data: any, ev?: DragEvent) => {
        // Check for FileDetailsVm
        if (data?.fileType && (data.fileType == "Image")) {
          let fileDetails: FileDetailsVm = data;
          this.syncedImage = fileDetails.filePath;
          this.syncedAltText = ""; // reset alt text when new image is uploaded
        } else
        // Check for raw file array (dragged from OS / Browser). The file list is converted to array in the directive
        if (Array.isArray(data) && data[0] && data[0].name) {
          this.handleDroppedFile(data[0]);
        } else {
          // Image dragged from a web page (e.g. Unsplash). Read the url synchronously, the drop data is not available after an await
          const url = this.getWebImageUrl(ev?.dataTransfer ?? null);
          if (!url) {
            this.$store.commit("ux/SB_FAILURE", {
              message: "Im gezogenen Inhalt wurde kein Bild gefunden.",
              timeout: 0
            });
            return;
          }
          const file = await this.downloadImageAsync(url);
          if (!file) {
            this.$store.commit("ux/SB_FAILURE", {
              message: "Das Bild konnte nicht von der Webseite übernommen werden. Bitte speichern Sie es und laden Sie es als Datei hoch.",
              timeout: 0
            });
            return;
          }
          this.handleDroppedFile(file);
        }
      }
    };
  }

  private handleDroppedFile(file: File) {
    // Check that it's an image file
    if (!this.isImageFile(file)) {
      this.$store.commit("ux/SB_FAILURE", {
        message: "Bei dieser Datei handelt es sich nicht um ein Bild.",
        timeout: 0
      });
      return;
    }
    if (this.uploadImage) {
      this.fileToUpload = file; // trigger upload overlay
    } else {
      // Shrink and convert to base64 for immediate display
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        this.syncedImage = await this.shrinkImageAsync(e.target.result);
        this.syncedAltText = ""; // reset alt text when new image is uploaded
        this.updateImageDisplayWidth(this.syncedImage);
      };
      reader.readAsDataURL(file);
    }
  }

  // Some files arrive without mime type (e.g. from Windows Explorer), fall back to the file extension
  private isImageFile(file: File): boolean {
    if (file.type) return file.type.startsWith('image/');
    return /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(file.name);
  }

  // Extract the image url of a drag from a web page
  private getWebImageUrl(dt: DataTransfer | null): string | null {
    if (!dt) return null;
    try {
      // Prefer the <img> of the dragged html, because a linked image (e.g. Unsplash) transfers the link target as uri-list
      const html = dt.getData('text/html');
      if (html) {
        const img = new DOMParser().parseFromString(html, 'text/html').querySelector('img');
        const src = img?.getAttribute('src');
        if (src && /^(https?:\/\/|data:image\/)/i.test(src)) return src;
      }
      const uri = (dt.getData('text/uri-list') || '').split(/\r?\n/).map(l => l.trim()).find(l => l && !l.startsWith('#'));
      if (uri && /^https?:\/\/.+\.(png|jpe?g|gif|webp|bmp|svg|avif)(\?.*)?$/i.test(uri)) return uri;
    } catch (ex) {
      return null;
    }
    return null;
  }

  // Download a web image as file. Fails if the server does not allow CORS
  private async downloadImageAsync(url: string): Promise<File | null> {
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) return null;
      const extension = blob.type.split('/')[1]?.split('+')[0] || 'jpg';
      let name = url.startsWith('data:') ? '' : (new URL(url).pathname.split('/').pop() || '');
      if (!/\.[a-z0-9]{2,5}$/i.test(name)) name = `${name || 'web-image'}.${extension}`;
      return new File([blob], name, { type: blob.type });
    } catch (ex) {
      return null;
    }
  }

  shrinkImageAsync(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const hasMaxWidth = this.maxImageWidth > 0;
        const hasMaxHeight = this.maxImageHeight > 0;

        if (!hasMaxWidth && !hasMaxHeight) {
          resolve(base64Image);
          return;
        }

        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        let scale = 1;
        if (img.width > this.maxImageWidth || img.height > this.maxImageHeight) {
          const maxWidth = hasMaxWidth ? this.maxImageWidth : Number.MAX_SAFE_INTEGER;
          const maxHeight = hasMaxHeight ? this.maxImageHeight : Number.MAX_SAFE_INTEGER;
          const scaleX = maxWidth / img.width;
          const scaleY = maxHeight / img.height;
          scale = Math.min(scaleX, scaleY);
        }
        width = img.width * scale;
        height = img.height * scale;
        // Create canvas to draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Could not get canvas context');
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const resizedBase64 = canvas.toDataURL('image/jpeg');
        resolve(resizedBase64);
      };
      img.onerror = (err) => {
        reject(err);
      };
      img.src = base64Image;
    });
  }

}
